import * as React from 'react';
import { useState } from 'react';
import * as API from '../../Utils/API';
import { formatMultiParagraphString, htmlIf } from '../../Utils/HTML';
import { PersonEvent, PersonEventCategory } from '../../Types/Person';
import JSONPretty from 'react-json-pretty';

type Props =
  { event: PersonEvent;

    /**
     * Called when an event is updated. Passes the updated event object to the parent.
     * @param updatedEvent - The event object with updated details.
     */
    onUpdate: (updatedEvent: PersonEvent) => void;
  }

function formatTime(dateString: string):string {
  const date = new Date(dateString)
  return (
    date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit'
    })
  )
}

function formatLastEdited(dateString: string):string {
  const date = new Date(dateString)
  return (
    date.toLocaleDateString('en-US', {
      day: '2-digit',
      month: 'numeric',
      year: '2-digit',
      hour: 'numeric',
      minute: '2-digit'
    })
  )
}

const EventCard = (props: Props) => {
  const event = props.event;

  const [isExpanded, setIsExpanded] = useState(false);
  const [isPropertiesExpanded, setIsPropertiesExpanded] = useState(false);

  const [eventNotes, setEventNotes] = useState(event.crmNotes);
  const [isEditingNotes, setIsEditingNotes] = useState(false);

  function saveEventNotes() {
    const postBody = {personEventId: event.id, eventNotes: eventNotes}
    API.post("admin_crm_set_event_notes_path", postBody).then(function (result) {
      if (result['error']) {
      } else {
        setIsEditingNotes(false);
      }
    })
  }

  const iconForCategory = (category: PersonEventCategory): string | undefined => ({
    [PersonEventCategory.CustomerSupport]: 'ai-headphone',
    [PersonEventCategory.Demo]: 'ai-calendar',
    [PersonEventCategory.TransactionalEmail]: 'ai-mail',
    [PersonEventCategory.CampaignEmail]: 'ai-send',
    [PersonEventCategory.BroadcastEmail]: 'ai-phone-call',
    [PersonEventCategory.Subscription]: 'ai-tag',
    [PersonEventCategory.ProjectRequest]: 'ai-briefcase',
    [PersonEventCategory.HourlyContract]: 'ai-briefcase',
    [PersonEventCategory.LeadMagnet]: 'ai-link',
    [PersonEventCategory.Livestream]: 'ai-monitor',
    [PersonEventCategory.Meeting]: 'ai-video',
    [PersonEventCategory.Revenue]: 'ai-dollar',
    [PersonEventCategory.PaymentMethod]: 'ai-card',
  })[category];

  return (
    <div key={event.id} className={`t--event-id-${event.id}`}>
      <div className="px-3 py-2 border-bottom">
        <div className="d-flex justify-content-between align-items-center cursor-pointer" onClick={() => setIsExpanded(!isExpanded)}>
          <div className="text-dark">
            <i className={`${iconForCategory(event.category) ?? 'ai-user'} fs-xxl me-2`} />
            {event.eventName}
          </div>
          <div className="">
            {formatTime(event.timestamp)}
            <i className={`ms-2 fs-xl ${isExpanded ? 'ai-chevron-up' : 'ai-chevron-down'}`} />
          </div>
        </div>
        {htmlIf(isExpanded,
          <div className="mt-2">
            <div className="" onClick={() => setIsPropertiesExpanded(!isPropertiesExpanded)}>
              <div className="d-flex align-items-center cursor-pointer mb-1">
                <div className="fs-md fw-semibold text-dark">Properties (click to expand)</div>
                <i className={`ms-1 fs-xl ${isPropertiesExpanded ? 'ai-chevron-up' : 'ai-chevron-down'}`} />
              </div>
              {htmlIf(isPropertiesExpanded,
                // See https://github.com/chenckang/react-json-pretty?tab=readme-ov-file#custom-style for styling information
                <JSONPretty
                  data={event.properties}
                  mainStyle='background-color: #edf1f5; color: black'
                  keyStyle='color: #0A66C2'
                  stringStyle='color: red'
                  valueStyle='color: #006400'
                />
              )}
            </div>
            <div className="mt-1 fs-sm">Category: {event.category}</div>
            <div className="mt-1 fs-sm">Person Event ID: {event.id}</div>
            <div className="mt-1 fs-sm">Timestamp: {event.timestamp}</div>
            <div className="mt-2 d-flex align-items-center justify-content-between">
              <div className="d-flex align-items-center">
                <div className="fw-semibold text-dark me-2">Event Notes</div>
                { isEditingNotes
                ? <button onClick={saveEventNotes} className="btn fs-md btn-link p-0">Save</button>
                : <button onClick={() => setIsEditingNotes(true)} className="ai-edit btn fs-xl btn-link p-0" />
                }
              </div>
              {htmlIf(props.event.crmNotes?.length > 0,
                <div className="">Last edited on {formatLastEdited(event.updatedAt)}</div>
              )}
            </div>
            { isEditingNotes
            ? <div className="mt-2">
                <textarea
                  className="form-control t--person-notes"
                  rows={4}
                  placeholder={'Enter notes…'}
                  onChange={(e) => setEventNotes(e.target.value) }
                  value={eventNotes || ''}
                />
              </div>
            : <div className="mt-2">{formatMultiParagraphString(eventNotes)}</div>
            }
          </div>
        )}
      </div>
    </div>
  )
}

export default EventCard;
